import React, { Suspense, useEffect, useState } from 'react';
import { AdaptiveDpr, PerformanceMonitor } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useStore } from '../store';
import { shared } from './shared';

// Auto quality-scaler: drop DPR + reduce particle FX when the frame-rate declines,
// restore it when headroom returns — keeps the growing ecosystem near 90/120 FPS.
function AutoQuality() {
  const setDpr = useThree((s) => s.setDpr);
  const gl = useThree((s) => s.gl);
  // Capture/photo mode: kill the shadow pass so heavy hero frames render fast.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('photo') || p.get('lowfx') === '1') {
      shared.lowFX = true;
      gl.shadowMap.enabled = false;
      gl.shadowMap.needsUpdate = true;
    }
  }, [gl]);
  return (
    <PerformanceMonitor
      onDecline={() => { shared.lowFX = true; setDpr(1); }}
      onIncline={() => { shared.lowFX = false; setDpr(Math.min(1.6, window.devicePixelRatio || 1)); }}
    />
  );
}

import WorldClock from './WorldClock';
import Terrain from './Terrain';
import Trees from './Trees';
import BankStructures from './BankStructures';
import Wildlife from './Wildlife';
import RiverLife from './RiverLife';
import Humans from './Humans';
import Ants from './Ants';
import Artifacts from './Artifacts';
import MediaOutlets from './MediaOutlets';
import Weather from './Weather';
import ZoneMarkers from './ZoneMarkers';
import HarpyEagle from './HarpyEagle';
import Bridge from './Bridge';
import SoundListener from './SoundListener';
import ProximityGuide from './ProximityGuide';
import ProfileGuide from './ProfileGuide';
import FieldGuide from './FieldGuide';
import NeutralChannelGuide from './NeutralChannelGuide';
import PostcardCapture from './PostcardCapture';
import WhaleFX from './WhaleFX';
import VRZoneMenu from './VRZoneMenu';
import Rig from './Rig';

export default function Scene() {
  const worldSeed = useStore((s) => s.worldSeed);
  // Lazy-mount: render the core terrain/trees first so the biome is instantly
  // walkable, then stream the living ecosystem + bridge in a beat later.
  const [heavy, setHeavy] = useState(false);
  const [wave, setWave] = useState(0);
  useEffect(() => {
    const t0 = setTimeout(() => setHeavy(true), 350);
    const t1 = setTimeout(() => setWave(1), 1000);  // ground herds
    const t2 = setTimeout(() => setWave(2), 1800);  // predators + basking
    return () => { clearTimeout(t0); clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return (
    <>
      <WorldClock />

      <Suspense fallback={null}>
        <Terrain />
        <group key={worldSeed}>
          <Trees />
          <BankStructures />
          {heavy && (
            <>
              <Wildlife wave={wave} />
              <RiverLife />
              <Humans />
              <Ants />
            </>
          )}
        </group>
        <Artifacts />
        {heavy && <Bridge />}
        <HarpyEagle />
        <Weather />
        <WhaleFX />
      </Suspense>

      {/* Text/label actors load a font async; isolate them so a slow font never
          blocks the biome geometry from rendering. */}
      <Suspense fallback={null}>
        <MediaOutlets />
        <ZoneMarkers />
      </Suspense>

      <SoundListener />
      <ProximityGuide />
      <ProfileGuide />
      <FieldGuide />
      <NeutralChannelGuide />
      <PostcardCapture />
      <VRZoneMenu />
      <Rig />

      <AdaptiveDpr pixelated />
      <AutoQuality />
    </>
  );
}
